// Infinite scroll v3.2, adapted: collection + search pages, accessible loading label, iOS-safe HTML cache cap.
if (!customElements.get("infinite-scroll")) {
  /** Pages where the component initialises: collection listing and search results (same `.collection_products_list` grid), tested on the path so a locale prefix still matches. */
  const SUPPORTED_PATHS = ["/collections/", "/search"];
  const isSupportedPath = (url) => SUPPORTED_PATHS.some((path) => url.includes(path));
  const APPENDING_CLASS = "infinite-scroll-appending";
  const CONTENT_VISIBILITY_CLASS = "infinite-scroll-cv";

  class InfiniteScrollComponent extends HTMLElement {
    constructor() {
      super();
      this.currentPage = 1;
      this.isLoading = false;
      this.hasMorePages = true;
      this.loadedProductsCount = 0;
      this.totalProductsCount = 0;
      this.gridObserver = null;
      this.lastProductsSignature = null;
      this.lastGridUrl = null;
      this._cacheJob = null;
      this._htmlCacheDisabled = false;
      this._disconnected = false;
      this._restorePending = false;
      this._loadGeneration = 0;
      this.options = {
        autoLoad: true,
        loadMoreText: "Voir plus",
        // Loading indicator label, a fallback only: sections pass the translation through `loading-text` since the JS cannot reach Liquid translations.
        loadingText: "Chargement",
        // Used only when loadingText is empty; both empty falls back to the CSS spinner.
        loadingImageUrl: "",
        scrollThreshold: -100,
        showProgress: false,

        productsContainer: ".collection_products_list",
        productSelector: ".grid_item",
        paginationSelector: ".pagination-wrapper",
        productCountSelector: "[data-products-count]",

        // Cards inserted per task: does not change products per page, only spreads the insertion cost.
        appendChunkSize: 8,
        // Needs `data-id="{{ section.id }}"` on the grid; falls back to the full page if the section response has no grid.
        useSectionRendering: false,
        // Measure contain-intrinsic-size in infinite-scroll.css before enabling.
        contentVisibility: false,
        eagerAppendedImages: true,
        // Max wait for the first visual of each appended card before revealing the batch, in ms.
        revealImageTimeout: 1200,

        storagePrefix: "InfiniteScroll_",
        // Restoring a huge DOM at once crashes iOS, so past this size the HTML is no longer cached and the catch-up refetches instead.
        htmlCacheMaxChars: 4000000,
        maxCatchUpPages: 20,
        // Pages requested in parallel during that catch-up.
        catchUpConcurrency: 4,
      };
    }

    connectedCallback() {
      if (!isSupportedPath(window.location.pathname)) return;
      this._disconnected = false;
      if ("scrollRestoration" in history) history.scrollRestoration = "manual";
      [
        ["auto-load", (val) => (this.options.autoLoad = val !== "false")],
        ["load-more-text", (val) => (this.options.loadMoreText = val)],
        ["loading-image", (val) => (this.options.loadingImageUrl = val)],
        ["loading-text", (val) => (this.options.loadingText = val)],
        ["threshold", (val) => (this.options.scrollThreshold = parseInt(val) || 0)],
        ["show-progress", (val) => (this.options.showProgress = val === "true")],
        ["product-count-selector", (val) => (this.options.productCountSelector = val)],
        ["product-selector", (val) => (this.options.productSelector = val)],
        ["pagination-selector", (val) => (this.options.paginationSelector = val)],
        ["append-chunk-size", (val) => (this.options.appendChunkSize = Math.max(1, parseInt(val) || this.options.appendChunkSize))],
        ["section-rendering", (val) => (this.options.useSectionRendering = val === "true")],
        ["content-visibility", (val) => (this.options.contentVisibility = val === "true")],
        ["cache-version", (val) => (this.options.cacheVersion = val)],
      ].forEach(([attributeName, setOption]) => this.hasAttribute(attributeName) && setOption(this.getAttribute(attributeName)));
      if (!this.initializeState(true)) return;
      this.setupUI();
      if (this.options.autoLoad) {
        this.boundCheckScroll = () => !this._suppressAutoLoad && this.productsContainer?.getBoundingClientRect().bottom - this.options.scrollThreshold <= window.innerHeight && this.loadNextPage();
        window.addEventListener("scroll", this.boundCheckScroll);
      }
      this.boundSaveOnHide = () => {
        this.saveAnchor();
        this.flushHtmlCache();
      };
      window.addEventListener("pagehide", this.boundSaveOnHide);
      this.boundSaveOnHidden = () => {
        if (document.visibilityState === "hidden") {
          this.saveAnchor();
          this.flushHtmlCache();
        }
      };
      document.addEventListener("visibilitychange", this.boundSaveOnHidden);
      this.boundHandlePageShow = () => {
        if (!this._restorePending) this.restoreScrollPosition();
      };
      window.addEventListener("pageshow", this.boundHandlePageShow);
      this.startRestore();
      this.observeGridChanges();
    }

    initializeState(useCache = false) {
      this.productsContainer = document.querySelector(this.options.productsContainer);
      this.pagination = document.querySelector(this.options.paginationSelector);
      if (!this.productsContainer) return false;
      if (this.options.contentVisibility) this.productsContainer.classList.add(CONTENT_VISIBILITY_CLASS);
      const productCountElement = document.querySelector(this.options.productCountSelector);
      if (productCountElement) {
        const match = productCountElement.textContent.trim().match(/\d+/);
        this.totalProductsCount = match ? parseInt(match[0]) : 0;
      }
      const cached = useCache && this.cache("get");
      // A stale version is ignored, not cleared: targetPage and the anchor survive and the catch-up refetches the new markup.
      const staleVersion = this.options.cacheVersion && this.cache("get", "version") !== this.options.cacheVersion;
      if (cached?.url === window.location.href && !staleVersion) {
        // Not innerHTML: Chrome connects the new custom elements before disconnecting the old ones, which breaks Depict's re-init and its content blocks.
        const parsed = document.createElement("template");
        parsed.innerHTML = cached.html;
        this.productsContainer.textContent = "";
        this.productsContainer.append(parsed.content);
        this.cleanSwiperTraces();
        this.currentPage = cached.currentPage;
        this.hasMorePages = cached.hasMorePages;
      } else {
        this.currentPage = 1;
        this.hasMorePages = this.checkHasMorePages(document);
      }
      this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
      // The container was just re-queried: the observer of the previous element died with it.
      if (this.gridObserver) this.observeGridChanges();
      return true;
    }

    setupUI() {
      if (!this.pagination) return;
      this.pagination.style.display = "none";
      this.removeLoadingElements();
      if (!this.options.autoLoad && this.hasMorePages) {
        this.loadMoreWrapper = this.createElementAfter(this.productsContainer, "div", { class: "infinite-scroll-load-more-wrapper" });
        if (this.options.showProgress) {
          this.productCountText = this.createAndAppend(this.loadMoreWrapper, "p", { class: "infinite-scroll-product-count" });
          this.progressBarBackground = this.createAndAppend(this.loadMoreWrapper, "div", { class: "infinite-scroll-progress-bar-background" });
          this.progressBar = this.createAndAppend(this.progressBarBackground, "div", { class: "infinite-scroll-progress-bar" });
        }
        this.loadMoreBtn = this.createAndAppend(this.loadMoreWrapper, "button", { class: "infinite-scroll-load-more btn btn_link", text: this.options.loadMoreText, onclick: () => this.loadNextPage() });
      }
      // The label is written on load then cleared, since a live region only announces a content change.
      this.loadingContainer = this.createElementAfter(this.loadMoreWrapper || this.productsContainer, "div", {
        class: "infinite-scroll-loading",
        role: "status",
        "aria-live": "polite",
        style: "display:none;",
      });
      if (this.options.loadingText) this.loadingText = this.createAndAppend(this.loadingContainer, "p", { class: "infinite-scroll-loading-text" });
      else if (this.options.loadingImageUrl) this.loadingImg = this.createAndAppend(this.loadingContainer, "img", { class: "infinite-scroll-loading-image", src: this.options.loadingImageUrl, alt: "", style: "display:none;" });
      else this.createAndAppend(this.loadingContainer, "span", { class: "infinite-scroll-loading-spinner", "aria-hidden": "true" });
      this.updateLoadingState(false);
    }

    getProducts() {
      return this.productsContainer ? Array.from(this.productsContainer.querySelectorAll(this.options.productSelector)) : [];
    }

    rememberTargetPage() {
      this.cache("set", { targetPage: this.currentPage, targetUrl: window.location.href });
    }

    startRestore() {
      const anchorIndex = this.cache("get", "anchorIndex");
      const anchorReady = Number.isFinite(anchorIndex) && anchorIndex >= 0 && anchorIndex < this.loadedProductsCount;
      if (anchorReady) this.restoreScrollPosition();
      // `pageshow` must not reposition before the catch-up has loaded the anchor card.
      else this._restorePending = true;
      this.catchUpToTargetPage().then(() => {
        if (!this._restorePending) return;
        this._restorePending = false;
        this.restoreScrollPosition();
      });
    }

    async catchUpToTargetPage() {
      if (this.isLoading) return false;
      if (this.cache("get", "targetUrl") !== window.location.href) return false;
      const target = parseInt(this.cache("get", "targetPage"), 10);
      if (!Number.isFinite(target) || target <= this.currentPage) return false;
      const lastPage = Math.min(target, this.currentPage + this.options.maxCatchUpPages);
      // Also blocks the scroll autoload, since loadNextPage exits while isLoading.
      this.isLoading = true;
      const gen = this._loadGeneration;
      this.updateLoadingState(true);
      try {
        // Fetched in parallel but inserted in order: the first missing or last page stops everything and later responses are dropped.
        const inFlight = new Map();
        let nextToFetch = this.currentPage + 1;
        for (let page = this.currentPage + 1; page <= lastPage && this.hasMorePages && !this.isStale(gen); page++) {
          while (nextToFetch <= lastPage && inFlight.size < this.options.catchUpConcurrency) {
            inFlight.set(
              nextToFetch,
              this.fetchPageDocument(nextToFetch).catch(() => null),
            );
            nextToFetch++;
          }
          const loaded = await inFlight.get(page);
          inFlight.delete(page);
          if (!loaded || this.isStale(gen) || !(await this.insertPage(page, loaded, gen, true))) break;
        }
        if (!this.hasMorePages) this.handleEndOfResults();
        if (lastPage === target) this.rememberTargetPage();
        this.scheduleHtmlCache();
      } catch {
      } finally {
        this.revealPending();
        if (gen === this._loadGeneration) {
          this.isLoading = false;
          this.updateLoadingState(false);
        }
      }
      return true;
    }

    saveAnchor() {
      const products = this.getProducts();
      let anchorIndex = -1;
      let anchorOffset = 0;
      // The first visible card is stored rather than a pixel offset, which drifts as images above it load.
      for (let i = 0; i < products.length; i++) {
        const rect = products[i].getBoundingClientRect();
        if (rect.bottom > 0) {
          anchorIndex = i;
          anchorOffset = Math.round(rect.top);
          break;
        }
      }
      this.cache("set", { scrollUrl: window.location.href, anchorIndex, anchorOffset });
    }

    restoreScrollPosition() {
      if (this.cache("get", "scrollUrl") !== window.location.href) return;
      const anchorIndex = this.cache("get", "anchorIndex");
      const anchorOffset = this.cache("get", "anchorOffset") || 0;
      // Only on a card that exists: a stored pixel position would overshoot a shorter grid and the clamped scrollTo would land at the bottom.
      if (!Number.isFinite(anchorIndex) || anchorIndex < 0) return;
      if (!this.getProducts()[anchorIndex]) return;

      this._suppressAutoLoad = true;
      let stopped = false;
      let ro = null;
      let stopTimer = null;
      const onLoadApply = () => apply();
      const stop = () => {
        if (stopped) return;
        stopped = true;
        this._suppressAutoLoad = false;
        window.removeEventListener("wheel", onUserScroll);
        window.removeEventListener("touchmove", onUserScroll);
        window.removeEventListener("keydown", onUserScroll);
        window.removeEventListener("load", onLoadApply);
        if (ro) ro.disconnect();
        clearTimeout(stopTimer);
      };
      // A real user gesture cancels the restoration.
      const onUserScroll = () => stop();

      const computeTarget = () => {
        const anchor = this.getProducts()[anchorIndex];
        return anchor ? anchor.getBoundingClientRect().top + window.scrollY - anchorOffset : null;
      };
      const apply = () => {
        if (stopped) return;
        const raw = computeTarget();
        if (raw === null) {
          stop();
          return;
        }
        const target = Math.max(0, raw);
        const maxReach = document.documentElement.scrollHeight - window.innerHeight;
        // Scroll only once the page can reach the target (else the browser clamps to the bottom), `instant` to bypass a theme `scroll-behavior: smooth`.
        if (maxReach >= target - 4 && Math.abs(window.scrollY - target) > 2) window.scrollTo({ top: target, behavior: "instant" });
      };

      if (window.ResizeObserver && this.productsContainer) {
        ro = new ResizeObserver(() => apply());
        ro.observe(this.productsContainer);
      }
      let frames = 0;
      const tick = () => {
        if (stopped) return;
        apply();
        if (++frames < 75) requestAnimationFrame(tick);
      };
      if (document.readyState !== "complete") window.addEventListener("load", onLoadApply, { once: true });
      window.addEventListener("wheel", onUserScroll, { passive: true, once: true });
      window.addEventListener("touchmove", onUserScroll, { passive: true, once: true });
      window.addEventListener("keydown", onUserScroll, { once: true });
      stopTimer = setTimeout(stop, 2500);
      apply();
      requestAnimationFrame(tick);
    }

    saveHtmlCache() {
      if (this._htmlCacheDisabled || !this.productsContainer) return;
      // The snapshot must hold neither the hiding class nor hundreds of `eager` images.
      this.revealPending();
      this.restoreLazyLoading();
      const html = this.productsContainer.innerHTML;
      // HTML first, metadata only once it is written: otherwise currentPage=10 could be stored with page 5's HTML and pages would be skipped.
      if (html.length > this.options.htmlCacheMaxChars || this.cache("set", "html", html) !== true) {
        this._htmlCacheDisabled = true;
        return;
      }
      // Stringified so that JSON.parse on read does not turn "1.0" into 1.
      this.cache("set", { currentPage: this.currentPage, hasMorePages: this.hasMorePages, url: window.location.href, version: JSON.stringify(this.options.cacheVersion ?? "") });
    }

    scheduleHtmlCache() {
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
      this.cancelHtmlCache();
      this._cacheJob = idle(() => {
        this._cacheJob = null;
        this.saveHtmlCache();
      });
    }

    flushHtmlCache() {
      if (!this._cacheJob) return;
      this.cancelHtmlCache();
      this.saveHtmlCache();
    }

    cancelHtmlCache() {
      if (!this._cacheJob) return;
      (window.cancelIdleCallback || clearTimeout)(this._cacheJob);
      this._cacheJob = null;
    }

    createElementAfter(ref, tag, attrs) {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([key, value]) => (key === "text" ? (el.textContent = value) : key === "onclick" ? el.addEventListener("click", value) : el.setAttribute(key, value)));
      ref.insertAdjacentElement("afterend", el);
      return el;
    }

    createAndAppend(parent, tag, attrs) {
      const el = this.createElementAfter(parent, tag, attrs);
      parent.appendChild(el);
      return el;
    }

    removeLoadingElements() {
      document.querySelectorAll(".infinite-scroll-load-more-wrapper, .infinite-scroll-load-more, .infinite-scroll-loading").forEach((el) => el.remove());
      this.loadMoreWrapper = this.loadMoreBtn = this.loadingContainer = this.loadingText = this.loadingImg = null;
    }

    yieldToBrowser() {
      // Not rAF: its callback runs before rendering, so it would still block the frame.
      if (window.scheduler && window.scheduler.yield) return window.scheduler.yield();
      return new Promise((resolve) => setTimeout(resolve, 0));
    }

    isStale(gen) {
      return this._disconnected || gen !== this._loadGeneration || !this.productsContainer.isConnected;
    }

    async appendInChunks(children, gen, catchUp = false) {
      const nodes = Array.from(children);
      const size = this.options.appendChunkSize;
      // Appended to the live grid, not a DocumentFragment: connectedCallback would otherwise fire all at once on the single insertion.
      const promoted = [];
      for (let i = 0; i < nodes.length; i += size) {
        const end = Math.min(i + size, nodes.length);
        for (let j = i; j < end; j++) {
          const card = nodes[j].cloneNode(true);
          card.classList.add(APPENDING_CLASS);
          // Catch-up cards are off screen: prioritising and waiting for their images is pointless.
          const img = !catchUp && this.promoteFirstImage(card);
          if (img) promoted.push(img);
          this.productsContainer.appendChild(card);
        }
        if (end >= nodes.length) break;
        await this.yieldToBrowser();
        if (this.isStale(gen)) {
          this.revealPending();
          return false;
        }
      }
      if (!catchUp) await this.waitForImages(promoted, this.options.revealImageTimeout);
      this.revealPending();
      return !this.isStale(gen);
    }

    promoteFirstImage(card) {
      if (!this.options.eagerAppendedImages) return null;
      const img = card.querySelector("img[loading='lazy']");
      if (img) img.setAttribute("loading", "eager");
      return img;
    }

    waitForImages(imgs, timeoutMs) {
      const pending = imgs.filter((img) => img && !img.complete);
      if (!pending.length) return Promise.resolve();
      return Promise.race([
        Promise.all(
          pending.map(
            (img) =>
              new Promise((resolve) => {
                img.addEventListener("load", resolve, { once: true });
                img.addEventListener("error", resolve, { once: true });
              }),
          ),
        ),
        new Promise((resolve) => setTimeout(resolve, timeoutMs)),
      ]);
    }

    restoreLazyLoading() {
      if (!this.options.eagerAppendedImages || !this.productsContainer) return;
      const imgs = this.productsContainer.querySelectorAll("img[loading='eager']");
      for (let i = 0; i < imgs.length; i++) imgs[i].setAttribute("loading", "lazy");
    }

    cleanSwiperTraces() {
      // A cached slider keeps its state classes and Swiper skips anything marked swiper-initialized, so the restored card would stay inert.
      const KEEP = /^(swiper-wrapper|swiper-slide|swiper-button-next|swiper-button-prev|swiper-scrollbar)$/;
      const inits = this.productsContainer.querySelectorAll(".swiper-initialized");
      for (let i = 0; i < inits.length; i++) {
        const nodes = [inits[i], ...inits[i].querySelectorAll("*")];
        for (let j = 0; j < nodes.length; j++) {
          const n = nodes[j];
          for (let k = n.classList.length - 1; k >= 0; k--) {
            const c = n.classList[k];
            if (c.indexOf("swiper-") === 0 && !KEEP.test(c)) n.classList.remove(c);
          }
          if (n.classList.contains("swiper-wrapper") || n.classList.contains("swiper-slide")) n.removeAttribute("style");
        }
      }
      return inits.length;
    }

    revealPending() {
      if (!this.productsContainer) return 0;
      const marked = this.productsContainer.querySelectorAll("." + APPENDING_CLASS);
      for (let i = 0; i < marked.length; i++) marked[i].classList.remove(APPENDING_CLASS);
      return marked.length;
    }

    async fetchDocument(pageNumber, useSection) {
      const startUrl = window.location.href;
      const url = new URL(startUrl);
      url.searchParams.set("page", pageNumber);
      const sectionId = useSection && this.productsContainer.getAttribute("data-id");
      if (sectionId) url.searchParams.set("section_id", sectionId);
      const gen = this._loadGeneration;
      const t0 = performance.now();
      const res = await fetch(url.toString(), { credentials: "same-origin" });
      if (!res.ok) return null;
      const html = await res.text();
      if (this.isStale(gen) || window.location.href !== startUrl) return null;
      const t1 = performance.now();
      const doc = new DOMParser().parseFromString(html, "text/html");
      return { doc, bytes: html.length, network: t1 - t0, parse: performance.now() - t1, section: !!sectionId };
    }

    async fetchPageDocument(pageNumber) {
      let res = await this.fetchDocument(pageNumber, this.options.useSectionRendering);
      if (!res) return null;
      let container = res.doc.querySelector(this.options.productsContainer);
      if (!container && res.section) {
        this.options.useSectionRendering = false;
        res = await this.fetchDocument(pageNumber, false);
        if (!res) return null;
        container = res.doc.querySelector(this.options.productsContainer);
      }
      return container?.children.length ? { res, container } : null;
    }

    async fetchPage(pageNumber) {
      const gen = this._loadGeneration;
      const loaded = await this.fetchPageDocument(pageNumber);
      return !!loaded && this.insertPage(pageNumber, loaded, gen);
    }

    async insertPage(pageNumber, { res, container }, gen, catchUp = false) {
      const t2 = performance.now();
      if (!(await this.appendInChunks(container.children, gen, catchUp))) return false;
      if (window.InfiniteScrollDebug) {
        console.log(`[infinite-scroll] page ${pageNumber} (${res.section ? "section" : "full page"}): network ${Math.round(res.network)}ms, parse ${Math.round(res.parse)}ms (${Math.round(res.bytes / 1024)}KB), insert ${Math.round(performance.now() - t2)}ms in chunks of ${this.options.appendChunkSize}`);
      }
      this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
      this.currentPage = pageNumber;
      this.hasMorePages = this.checkHasMorePages(res.doc);
      this.lastProductsSignature = this.getProductsSignature();
      return true;
    }

    async loadNextPage() {
      // Lock set before the first await so two scroll events in the same task cannot both pass.
      if (this.isLoading || !this.hasMorePages) return;
      this.isLoading = true;
      const gen = this._loadGeneration;
      this.updateLoadingState(true);
      try {
        if (!(await this.fetchPage(this.currentPage + 1))) return this.handleEndOfResults();
        if (!this.hasMorePages) this.handleEndOfResults();
        // Before the HTML: tiny, so never refused by the quota.
        this.rememberTargetPage();
        this.scheduleHtmlCache();
      } catch {
        this.handleEndOfResults();
      } finally {
        this.revealPending();
        if (gen === this._loadGeneration) {
          this.isLoading = false;
          this.updateLoadingState(false);
        }
      }
    }

    checkHasMorePages(doc) {
      const container = doc.querySelector(this.options.productsContainer);
      return container?.children.length > 0 && !!doc.querySelector(`a[href*="page=${this.currentPage + 1}"], .pagination a[rel="next"], .pagination .next:not(.disabled)`);
    }

    updateLoadingState(isLoading) {
      if (this.loadMoreBtn) this.loadMoreBtn.disabled = isLoading;
      if (this.loadMoreWrapper) this.loadMoreWrapper.style.display = isLoading ? "none" : "flex";
      if (this.loadingContainer) this.loadingContainer.style.display = isLoading ? "flex" : "none";
      if (this.loadingText) this.loadingText.textContent = isLoading ? this.options.loadingText : "";
      if (this.loadingImg) this.loadingImg.style.display = isLoading ? "block" : "none";
      if (this.productCountText && this.totalProductsCount > 0) this.productCountText.textContent = `${this.loadedProductsCount} products loaded out of ${this.totalProductsCount}`;
      if (this.progressBar && this.totalProductsCount > 0) this.progressBar.style.width = `${(this.loadedProductsCount / this.totalProductsCount) * 100}%`;
    }

    handleEndOfResults() {
      this.hasMorePages = false;
      this.removeLoadingElements();
    }

    cache(action, key, value) {
      const formatKey = (s) => this.options.storagePrefix + s;
      const serialize = (val) => (typeof val === "object" ? JSON.stringify(val) : String(val));
      const deserialize = (val) => {
        try {
          return val ? JSON.parse(val) : null;
        } catch {
          return val;
        }
      };
      try {
        if (action === "get") {
          if (!key) {
            const vals = Object.fromEntries(["url", "html", "hasMorePages", "currentPage", "scrollUrl"].map((k) => [k, deserialize(sessionStorage.getItem(formatKey(k)))]));
            return vals.url && vals.html ? vals : null;
          }
          return deserialize(sessionStorage.getItem(formatKey(key)));
        }
        if (action === "set") {
          if (typeof key === "object") Object.entries(key).forEach(([k, val]) => sessionStorage.setItem(formatKey(k), serialize(val)));
          else sessionStorage.setItem(formatKey(key), serialize(value));
          // Confirmed write: saveHtmlCache relies on it to detect the quota.
          return true;
        }
        if (action === "clear")
          Object.keys(sessionStorage)
            .filter((k) => k.startsWith(this.options.storagePrefix))
            .forEach((k) => sessionStorage.removeItem(k));
      } catch {
        if (action === "set") return false;
      }
      return null;
    }

    observeGridChanges() {
      if (!this.productsContainer) return;
      if (this.gridObserver) this.gridObserver.disconnect();
      this.lastProductsSignature = this.getProductsSignature();
      this.lastGridUrl = window.location.href;
      this.gridObserver = new MutationObserver(() => this.onGridMutation());
      this.gridObserver.observe(this.productsContainer, { childList: true });
    }

    onGridMutation() {
      if (this.isLoading) return;
      const signature = this.getProductsSignature();
      if (signature === this.lastProductsSignature) return;
      // Depict reorders cards in place without touching the URL: only a grid change that comes with a new URL is a filter change.
      if (window.location.href !== this.lastGridUrl && isSupportedPath(window.location.pathname)) this.handleFilterChange();
      this.lastProductsSignature = this.getProductsSignature();
      this.lastGridUrl = window.location.href;
    }

    getProductsSignature() {
      const products = this.productsContainer ? this.productsContainer.querySelectorAll(this.options.productSelector) : [];
      if (!products.length) return "0";
      const idOf = (el) => el.querySelector("[data-product-id]")?.dataset.productId || el.querySelector('a[href*="/products/"]')?.getAttribute("href") || "";
      return `${products.length}|${idOf(products[0])}|${idOf(products[products.length - 1])}`;
    }

    handleFilterChange() {
      // No pending write may land after the clear, and any in-flight load targeted the previous grid.
      this.cancelHtmlCache();
      this.cache("clear");
      this._htmlCacheDisabled = false;
      this._loadGeneration++;
      this.isLoading = false;
      this.initializeState(false);
      this.setupUI();
    }

    disconnectedCallback() {
      this._disconnected = true;
      if (this.boundCheckScroll) window.removeEventListener("scroll", this.boundCheckScroll);
      if (this.boundSaveOnHide) window.removeEventListener("pagehide", this.boundSaveOnHide);
      if (this.boundSaveOnHidden) document.removeEventListener("visibilitychange", this.boundSaveOnHidden);
      if (this.boundHandlePageShow) window.removeEventListener("pageshow", this.boundHandlePageShow);
      this.cancelHtmlCache();
      if (this.gridObserver) this.gridObserver.disconnect();
    }
  }
  customElements.define("infinite-scroll", InfiniteScrollComponent);
}
